import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  InfoCardWithAvatar,
  showNotif,
  Typography,
  useRightSideBar,
  UserInfo
} from '@p81-common/ui';
import { links, platformRoles } from 'data/constants';
import { getInitialsFull } from 'utils/common';
import type { User } from '../../helpers/types';
import createClasses from './styles';
import { postRequest, putRequest } from 'utils/requests';
import { PlatformTeamReq } from '../../helpers/helpers';
import { useContent } from 'contexts/content-context';
import { omit } from 'lodash';

export interface Filter {
  fieldName: string;
  direction: number;
  currentPage: number;
  itemsPerPage: number;
  search: string;
  action: never[];
  status: never[];
  updatedByName: never[];
  datetime: {
    startDate: null;
    endDate: null;
  };
}

type Props = {
  user: User;
  updateTableData: Dispatch<SetStateAction<any[]>>;
  filters: Record<string, unknown>;
  pagination: Filter;
};

export const ChangeUserRole = memo(function ChangeUserRole(props: Props) {
  const { user, updateTableData, filters, pagination } = props;
  const styles = createClasses();
  const { t } = useTranslation(['activity']);

  const allRoles = omit(platformRoles, 'ADMIN_DEMO');
  const currentUserRole =
    user.role === allRoles.ADMIN ? allRoles.OWNER : user.role;

  const [newUserRole, setNewUserRole] = useState(currentUserRole);
  const [submitting, setSubmitting] = useState(false);
  const {
    contentState: { customerData }
  } = useContent();
  const fullName = `${user.firstName} ${user.lastName}`;
  const { setOpenSecondary, setShowBottomActionBar } = useRightSideBar();
  const availableRoles = listAvailableRoles(customerData, allRoles);

  const updateTable = useCallback(
    async (customerId: string) => {
      const linkForRequest = `/customer/platform/team/list?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`;
      const REQ_BODY = PlatformTeamReq(filters, customerId);
      const res = await postRequest(linkForRequest, REQ_BODY);
      updateTableData(res.body.data);
    },
    [pagination.currentPage, pagination.itemsPerPage, filters, updateTableData]
  );

  const avatarProps =
    user.icon === 'NA'
      ? {
          initials: getInitialsFull(fullName),
          backgroundColor: user.initialscolor
        }
      : { src: user.icon };

  const onCancelClick = useCallback(() => {
    setOpenSecondary(false);
  }, [setOpenSecondary]);

  const onApplyClick = useCallback(async () => {
    try {
      setSubmitting(true);
      const requestUrl = '/customer/platform/team/role';
      const { userid, customerId } = user;
      const body = {
        userId: userid,
        role: newUserRole,
        customerId
      };

      await putRequest(requestUrl, body);
      showNotif(t('activity:ROLES.SUCCESS_MESSAGE'), {
        alertProps: {
          variant: 'filled',
          severity: 'success'
        }
      });
      await updateTable(customerId);
      setSubmitting(false);
      setOpenSecondary(false);
    } catch (e) {
      showNotif(t('activity:ROLES.FAILURE_MESSAGE'), {
        alertProps: {
          variant: 'filled',
          severity: 'error'
        }
      });
      setSubmitting(false);
    }
  }, [newUserRole, user, t, setOpenSecondary, updateTable]);

  useEffect(() => {
    setShowBottomActionBar({
      okButtonProps: {
        text: t('activity:common.APPLY'),
        onClick: onApplyClick,
        disabled: submitting || newUserRole === currentUserRole
      },
      cancelButtonProps: {
        text: t('activity:common.CANCEL'),
        onClick: onCancelClick,
        disabled: submitting
      },
      isSideBar: true
    });
  }, [t, setShowBottomActionBar, onCancelClick, onApplyClick, submitting, newUserRole, currentUserRole]);

  return (
    <Grid container direction="column">
      <Typography variant="h2" className={styles.changeRoleTitle}>
        Change Role
      </Typography>
      <UserInfo
        avatarProps={avatarProps}
        name={fullName}
        userRole={user.role}
        userEmail={user.email}
        status="Online"
      />
      {availableRoles.map((role, i) => {
        return (
          <Grid item key={role} className={styles.roleButton}>
            <InfoCardWithAvatar
              activ={newUserRole === role}
              key={i}
              onClick={() => {
                setNewUserRole(role);
              }}
              title={t(`activity:ROLES.NAMES.${role.toUpperCase()}`)}
              subtitle={t(`activity:ROLES.DESCRIPTION.${role.toUpperCase()}`)}
              avatarProps={{
                icon: {
                  src: `${
                    links.imagesBaseUrl
                  }/icons2/icon_${role.toLowerCase()}.svg`
                }
              }}
            />
          </Grid>
        );
      })}
    </Grid>
  );
});

function listAvailableRoles(
  customerData: Record<string, unknown>,
  allRoles: Record<string, string>
) {
  const { tenantUUID, customerType } = customerData;
  let availableRoles = Object.values(omit(allRoles, 'ADMIN'));
  const isCheckpoint = !!tenantUUID;
  const isMspChild = customerType === 'Child';
  if (isCheckpoint || isMspChild) {
    availableRoles = availableRoles.filter(
      role => role === allRoles.USER || role === allRoles.MANAGER
    );
  }

  const sortOrder: { [key: string]: number } = { U: 1, B: 2, M: 3, A: 4 };
  availableRoles.sort((a, b) => {
    return (sortOrder[a[0]] || 5) - (sortOrder[b[0]] || 5);
  });
  return availableRoles;
}
